// Synonym + phrase expansion for global search. Mirrors the Ruby
// SearchService.expand_synonyms in the backend so action/Cmd-K matching on the
// client uses the same vocabulary as record search on the server.
//
// Source of truth lives with the backend at
// childcareCRM/backend/childcare_api/config/search_synonyms.json so the Rails
// app can load it as a normal config. Cross-language parity is enforced by
// fixture-based tests on both sides.

import synonymData from "../../backend/childcare_api/config/search_synonyms.json";

interface SynonymConfig {
  nickname_groups: string[][];
  incident_groups: string[][];
  domain_groups: string[][];
  phrase_synonyms: Record<string, string[]>;
}

const config = synonymData as unknown as SynonymConfig;

export const MAX_VARIANTS = 8;

function buildSynonymMap(groups: readonly (readonly string[])[]): Record<string, readonly string[]> {
  const map: Record<string, string[]> = {};
  for (const group of groups) {
    for (const term of group) {
      if (!map[term]) map[term] = [];
      map[term].push(...group);
    }
  }
  const frozen: Record<string, readonly string[]> = {};
  for (const [k, v] of Object.entries(map)) {
    frozen[k] = Array.from(new Set(v));
  }
  return frozen;
}

function mergeMaps(...maps: Record<string, readonly string[]>[]): Record<string, readonly string[]> {
  const out: Record<string, string[]> = {};
  for (const m of maps) {
    for (const [k, v] of Object.entries(m)) {
      if (!out[k]) out[k] = [];
      out[k].push(...v);
    }
  }
  const frozen: Record<string, readonly string[]> = {};
  for (const [k, v] of Object.entries(out)) {
    frozen[k] = Array.from(new Set(v));
  }
  return frozen;
}

export const NICKNAME_MAP = buildSynonymMap(config.nickname_groups);
export const INCIDENT_SYNONYM_MAP = buildSynonymMap(config.incident_groups);
export const DOMAIN_MAP = buildSynonymMap(config.domain_groups);
export const SYNONYM_MAP = mergeMaps(NICKNAME_MAP, INCIDENT_SYNONYM_MAP, DOMAIN_MAP);
export const PHRASE_SYNONYMS: Record<string, readonly string[]> = config.phrase_synonyms;

// Returns a list of query variants including the original. Multi-word phrases
// are matched first against PHRASE_SYNONYMS; otherwise each token is expanded
// against SYNONYM_MAP. Variants are the Cartesian product of token expansions,
// capped at MAX_VARIANTS. Mirrors SearchService.expand_synonyms in Ruby.
export function expandSynonyms(query: string | null | undefined): string[] {
  const original = (query ?? "").trim();
  if (!original) return [""];

  const normalized = original.toLowerCase().replace(/\s+/g, " ");

  const phraseHit = PHRASE_SYNONYMS[normalized];
  if (phraseHit) {
    return uniq([original, ...phraseHit]).slice(0, MAX_VARIANTS + 1);
  }

  const tokens = normalized.split(/\s+/);
  const tokenOptions = tokens.map((t) => SYNONYM_MAP[t] ?? [t]);

  let variants: string[][] = [[]];
  for (const options of tokenOptions) {
    variants = variants.flatMap((prefix) => options.map((opt) => [...prefix, opt]));
    if (variants.length > MAX_VARIANTS) break;
  }
  const expanded = variants.slice(0, MAX_VARIANTS).map((toks) => toks.join(" "));
  return uniq([original, ...expanded]);
}

function uniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}
